import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useViewer } from '@treams/treams-hooks';
import { ArrowLeftIcon, DialogueIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	NoPrintWrapper,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	QuillText,
	Skeleton,
	Title,
	Toolbar,
	tokens,
} from '@treams/treams-ui';

import FeedbackResultQuestion from '../FeedbackResultQuestion';
import { FeedbackShareModal } from '../FeedbackShareModal';
import { FeedbackResultsActions, FeedbackResultsToolbar } from './FeedbackResultsToolbar';

const Results = styled(Container)`
	@media print {
		padding-top: ${tokens.padding.section}px !important;
		padding-bottom: ${tokens.padding.section}px !important;
	}
`;

const TitleBlock = styled(Box)`
	@media print {
		margin-top: 0 !important;
	}
`;

const Divider = styled(Box)`
	height: 1px;
	width: 100%;
	background: ${(props) => props.theme.tokens.border.subtle};

	@media print {
		display: none;
	}
`;

const QuestionBlock = styled(Box)`
	@media print {
		margin-top: ${tokens.gap.section}px !important;
	}
`;

const TitleIcon = styled(Box)`
	width: 40px;
	height: 40px;
	flex-shrink: 0;

	svg {
		display: block;
		width: 40px;
		height: 40px;

		path {
			fill: ${(props) => props.theme.tokens.icon.primary};
		}
	}
`;

type Props = {
	request: graphql.FeedbackRequest;
};

export function FeedbackResults(props: Props) {
	const { t } = useTranslation();
	const location = useLocation();
	const viewer = useViewer();

	const [sharing, setSharing] = useState(false);

	const responses = _.sortBy(
		props.request.responses.edges.map(({ node }) => node),
		(response) => `${response.recipient.firstName} ${response.recipient.lastName}`,
	);

	const responseID = new URLSearchParams(location.search).get('response_id');
	const response = responseID ? responses.find(({ id }) => id === responseID) : undefined;

	const isUnreleased =
		!!props.request.releaseDate && new Date(props.request.releaseDate).getTime() > Date.now();
	const isSubject = viewer?.id === props.request.user.id;
	const canSeeUnreleased = !isSubject && props.request.user.viewerCanViewFeedback;

	const visibleQuestions = props.request.questions.filter(
		(question) => (question.canSubjectView ?? true) || !isSubject,
	);

	return (
		<Results py={4} px={4} width="100%" wide={PAGE_CONTENT_WIDTH}>
			<FeedbackResultsToolbar request={props.request} setSharing={setSharing} />
			{isUnreleased &&
				(canSeeUnreleased ? (
					<Callout color="blue" mt={4}>
						{t('feedback.results.releaseDate.calloutViewer', {
							date: moment(props.request.releaseDate).format('LL'),
						})}
					</Callout>
				) : (
					<Callout color="warning" mt={4}>
						{t('feedback.results.releaseDate.callout', {
							date: moment(props.request.releaseDate).format('LL'),
						})}
					</Callout>
				))}
			<TitleBlock mt={4}>
				<Flex alignItems="center" style={{ gap: 12 }}>
					<TitleIcon>
						<DialogueIcon />
					</TitleIcon>
					<Title>{props.request.title || t('feedback.incoming.card.title')}</Title>
				</Flex>
				{props.request.instruction && <QuillText value={props.request.instruction} />}
				<FeedbackResultsActions request={props.request} setSharing={setSharing} />
				<Divider mt={5} mb={5} />
				{visibleQuestions.map((question, index) => (
					<QuestionBlock mt={index === 0 ? 0 : 5} key={question.id}>
						<FeedbackResultQuestion
							index={index + 1}
							request={props.request}
							question={question}
							response={response ?? undefined}
							responses={responses}
							isSubject={isSubject}
						/>
					</QuestionBlock>
				))}
			</TitleBlock>
			{sharing && (
				<FeedbackShareModal feedbackRequest={props.request} onDone={() => setSharing(false)} />
			)}
		</Results>
	);
}

export function FeedbackResultsSkeleton() {
	const { t } = useTranslation();

	return (
		<Container py={4} px={4} width="100%" wide={PAGE_CONTENT_WIDTH}>
			<NoPrintWrapper>
				<Toolbar>
					<Button
						icon={<ArrowLeftIcon />}
						link={'/feedback'}
						title={t('feedback.show.toolbar.back')}
						type="tertiary"
						ml="-12px"
					/>
				</Toolbar>
			</NoPrintWrapper>
			<Box mt={4}>
				<Title.Skeleton mb={3} />
				<Paragraph.Skeleton />
				<Skeleton.Repeat count={3}>
					<Box mt={4}>
						<FeedbackResultQuestion.Skeleton />
					</Box>
				</Skeleton.Repeat>
			</Box>
		</Container>
	);
}
